<template>
  <div class="pricing-container" @close="close">
    <div v-show="!hidePricingSurface">
      <h2 class="title">Self-Serve Plans</h2>
      <p class="subtitle">Flexible monthly or yearly billing</p>

      <div class="billing-toggle">
        <button
          class="billing-option"
          :class="{ active: isYearly }"
          @click="isYearly = true"
        >
          <span>Yearly Billing</span>
          <span class="discount-label">{{ yearlyDiscountLabel }}</span>
        </button>
        <button
          class="billing-option"
          :class="{ active: !isYearly }"
          @click="isYearly = false"
        >
          Monthly Billing
        </button>
      </div>

      <div class="plans" v-if="saasTiers.length">
        <div class="plan" v-for="tier in saasTiers" :key="tier.id">
          <div
            class="card"
            :class="{
              active: isCurrentTier(tier),
              featured: tier.featured || tier.id === selectedPlan
            }"
          >
            <div class="plan-header">
              <h5 class="plan-title">{{ tier.label }}</h5>
              <span v-if="isCurrentTier(tier)" class="current-badge"
                >Current</span
              >
              <span v-else-if="tier.featured" class="popular-badge"
                >Most Popular</span
              >
            </div>

            <h3 class="plan-price">
              <template v-if="tier.id === 'enterprise'">Custom</template>
              <template v-else>
                €{{ displayMonthlyPrice(tier) }}<small>/month</small>
              </template>
            </h3>

            <p
              class="billing-info"
              v-if="tier.id === 'base' || tier.id === 'pro'"
            >
              Billed {{ isYearly ? "yearly" : "monthly" }}
            </p>
            <p
              v-else
              class="billing-info billing-info--placeholder"
              aria-hidden="true"
            >
              &nbsp;
            </p>

            <button
              v-if="isCurrentTier(tier)"
              class="subscribe-btn btn-current"
              disabled
            >
              Current plan
            </button>
            <button
              v-else
              class="subscribe-btn"
              :class="tier.featured ? 'btn-primary' : 'btn-outline-primary'"
              @click="onTierCta(tier)"
            >
              {{ ctaLabel(tier) }}
            </button>

            <ul class="feature-list">
              <li
                v-for="(feature, idx) in tier.features"
                :key="feature"
                :class="{
                  'feature-key': idx < keyFeatureCount(tier),
                  'feature-divider-above':
                    idx === keyFeatureCount(tier) &&
                    tier.features.length > keyFeatureCount(tier)
                }"
              >
                <span class="feature-check">✓</span>
                <span class="feature-text">{{ feature }}</span>
              </li>
            </ul>

            <p v-if="tier.notes && tier.notes.length" class="plan-notes">
              <span
                v-for="(note, noteIndex) in tier.notes"
                :key="note"
                :class="[
                  'plan-note',
                  note.startsWith('**') ? 'plan-note--new-line' : '',
                  note.toLowerCase().includes('coming soon')
                    ? 'plan-note--badge'
                    : ''
                ]"
              >
                <template v-if="contactNoteParts(note)">
                  {{ contactNoteParts(note).before
                  }}<a
                    class="plan-note-link"
                    href="mailto:info@dicom.vision"
                    >Contact us</a
                  >{{ contactNoteParts(note).after }}
                </template>
                <template v-else>{{ note }}</template
                ><span
                  v-if="noteIndex < tier.notes.length - 1"
                  class="plan-note-separator"
                  >·</span
                >
              </span>
            </p>
          </div>
        </div>
      </div>
      <p v-if="saasTiers.length" class="usage-policy-note">
        Usage limits reset monthly, including on annual plans. AI outputs
        require clinical review and do not replace diagnostic reports.
      </p>

      <hr v-if="enterpriseTier" class="section-divider" />
      <section v-if="enterpriseTier" class="enterprise-qualifier">
        <div class="enterprise-content">
          <h3 class="enterprise-title">
            Enterprise Solutions
          </h3>
          <p class="enterprise-subtitle">
            Built for healthcare, research and telemedicine environments.
          </p>
          <p class="enterprise-description">
            On-premise or cloud deployments supporting advanced clinical, AI,
            and telemedicine workflows with scalable data infrastructure.
          </p>
        </div>
        <div class="enterprise-actions">
          <button class="enterprise-btn" @click="openEnterpriseModal">
            Contact Sales
          </button>
          <button class="enterprise-link" @click="openEnterpriseModal">
            Explore Enterprise →
          </button>
        </div>
      </section>
    </div>

    <v-dialog
      v-model="showEnterpriseModal"
      max-width="980"
      width="98vw"
      overlay-color="black"
      overlay-opacity="0.7"
      content-class="enterprise-inquiry-dialog"
    >
      <v-card class="enterprise-dialog-card" dark>
        <v-card-title class="headline enterprise-dialog-title">
          {{
            enterpriseSubmitted
              ? "Enterprise request received"
              : "Enterprise Deployment & Regulated Medical Device Compliance"
          }}
        </v-card-title>
        <v-card-text v-if="!enterpriseSubmitted" class="enterprise-dialog-body">
          <EnterpriseInquiryForm
            v-model="enterpriseForm"
            :deployment-type-options="deploymentTypeOptions"
            :estimated-storage-options="estimatedStorageOptions"
          />
        </v-card-text>

        <v-card-text v-else class="enterprise-dialog-success">
          <div class="enterprise-success-note">
            <p class="mb-2">Thank you. We'll contact you shortly.</p>
            <p class="mb-0 enterprise-success-question">
              Need a faster response? You can book a call with our team.
            </p>
          </div>
        </v-card-text>

        <v-card-actions>
          <template v-if="enterpriseSubmitted">
            <v-spacer />
            <v-btn
              text
              class="dialog-close-btn"
              @click="showEnterpriseModal = false"
              >Close</v-btn
            >
            <v-btn
              class="enterprise-booking-btn"
              color="primary"
              href="https://calendar.app.google/ypXzT7MmkX51kQnM8"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a Call
            </v-btn>
          </template>
          <template v-else>
            <v-spacer />
            <v-btn
              text
              class="dialog-close-btn"
              @click="showEnterpriseModal = false"
              >Close</v-btn
            >
            <v-btn
              class="enterprise-submit-btn"
              color="primary"
              @click="submitEnterprise"
            >
              Submit
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import EnterpriseInquiryForm from "@/components/EnterpriseInquiryForm.vue";

export default {
  name: "PurchaseSubscription",
  components: {
    EnterpriseInquiryForm
  },
  props: {
    openEnterpriseToken: {
      type: Number,
      default: 0
    },
    inquiryOnly: {
      type: Boolean,
      default: false
    },
    presetDeploymentType: {
      type: String,
      default: ""
    },
    selectedPlan: { type: String, default: "" },
    selectedInterval: { type: String, default: "" },
    source: { type: String, default: "" },
    campaign: { type: String, default: "" }
  },
  data() {
    return {
      isYearly: true,
      showEnterpriseModal: false,
      enterpriseSubmitted: false,
      enterpriseCalendlyUrl: "",
      enterpriseForm: {
        name: "",
        work_email: "",
        organization: "",
        role: "",
        country: "",
        deployment_type: [],
        estimated_users: "",
        estimated_storage: "",
        message: ""
      }
    };
  },
  computed: {
    ...mapGetters("billing", ["catalog", "isBillingEnabled"]),
    ...mapGetters("auth", ["isAuth"]),
    authUser() {
      return this.$store?.state?.auth?.user || null;
    },
    yearlyDiscountLabel() {
      return this.catalog?.yearly_discount_label || "Save 20%";
    },
    tiers() {
      return this.catalog?.tiers || [];
    },
    saasTiers() {
      return this.tiers.filter(tier => tier.id !== "enterprise");
    },
    enterpriseTier() {
      return this.tiers.find(tier => tier.id === "enterprise");
    },
    deploymentTypeOptions() {
      return this.catalog?.enterprise?.fields?.deployment_type || [];
    },
    estimatedStorageOptions() {
      return this.catalog?.enterprise?.fields?.estimated_storage || [];
    },
    currentTier() {
      return (
        this.$store.state.auth.user?.subscription_tier || "free"
      ).toLowerCase();
    },
    hidePricingSurface() {
      return this.inquiryOnly && this.showEnterpriseModal;
    },
    isFreeUser() {
      return this.currentTier === "free";
    }
  },
  async mounted() {
    if (!this.catalog) {
      await this.$store.dispatch("billing/fetchBillingCatalog");
    }
    if (this.openEnterpriseToken > 0) {
      this.applyPresetDeployment(this.presetDeploymentType);
      this.openEnterpriseModal();
    }
    if (this.selectedInterval) this.isYearly = this.selectedInterval === "yearly";
  },
  watch: {
    selectedInterval(value) {
      if (value) this.isYearly = value === "yearly";
    },
    openEnterpriseToken(value, previousValue) {
      if (value === previousValue) return;
      this.applyPresetDeployment(this.presetDeploymentType);
      this.openEnterpriseModal();
    },
    showEnterpriseModal(value) {
      if (value) {
        this.prefillEnterpriseContactFields();
      }
      this.$emit("enterprise-modal-visibility", value);
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    isCurrentTier(tier) {
      return this.currentTier === tier.id;
    },
    displayMonthlyPrice(tier) {
      if (!tier.price) return "0";
      return this.isYearly ? tier.price.yearly_eur : tier.price.monthly_eur;
    },
    ctaLabel(tier) {
      if (tier.id === "free" && !this.isFreeUser) {
        return "Switch to Free";
      }
      if (this.isFreeUser || tier.cta?.action !== "checkout") {
        return tier.cta?.label || "Select";
      }
      if (tier.id === "base") return "Switch to Basic";
      if (tier.id === "pro") return "Switch to Pro";
      return tier.cta?.label || "Select";
    },
    contactNoteParts(note) {
      const marker = "Contact us";
      if (!note || !note.includes(marker)) {
        return null;
      }
      const index = note.indexOf(marker);
      return {
        before: note.slice(0, index),
        after: note.slice(index + marker.length)
      };
    },
    async onTierCta(tier) {
      if (!this.isBillingEnabled) {
        return;
      }
      if (tier.cta.action === "enterprise_inquiry") {
        this.showEnterpriseModal = true;
        this.enterpriseSubmitted = false;
        this.enterpriseCalendlyUrl =
          this.catalog?.enterprise?.calendly_url || "";
        return;
      }
      if (tier.cta.action !== "checkout") {
        return;
      }

      const priceId = this.isYearly
        ? tier.price?.price_id?.yearly
        : tier.price?.price_id?.monthly;
      if (!priceId) {
        this.$store.commit("raiseSnackbar", {
          text: "Selected plan is not available in this environment.",
          color: "warning",
          timeout: 4000
        });
        return;
      }

      try {
        const response = await Vue.$http.post(
          "api/create-checkout-session/",
          null,
          { price_id: priceId, source: this.source, campaign: this.campaign }
        );
        const payload = response?.data || response?.body || response;
        if (payload?.url) {
          window.location.href = payload.url;
        }
      } catch (error) {
        this.$store.commit("raiseSnackbar", {
          text: "Checkout error. Please try again.",
          color: "error",
          timeout: 4000
        });
        console.error(error);
      }
    },
    keyFeatureCount(tier) {
      if (tier.id === "pro") return 6;
      if (tier.id === "base") return 5;
      if (tier.id === "free") return 4;
      return 3;
    },
    openEnterpriseModal() {
      if (!this.enterpriseTier) return;
      this.onTierCta(this.enterpriseTier);
    },
    prefillEnterpriseContactFields() {
      if (!this.isAuth || !this.authUser) {
        return;
      }
      const userName =
        typeof this.authUser.name === "string" ? this.authUser.name.trim() : "";
      const userEmail =
        typeof this.authUser.email === "string" ? this.authUser.email.trim() : "";

      this.enterpriseForm = {
        ...this.enterpriseForm,
        name: userName || this.enterpriseForm.name,
        work_email: userEmail || this.enterpriseForm.work_email
      };
    },
    applyPresetDeployment(option) {
      if (!option) return;
      this.enterpriseForm = {
        ...this.enterpriseForm,
        deployment_type: [option]
      };
    },
    async submitEnterprise() {
      const required = [
        "name",
        "work_email",
        "organization",
        "role",
        "country",
        "estimated_users",
        "estimated_storage"
      ];
      const missing = required.filter(key => !this.enterpriseForm[key]);
      if (missing.length || !this.enterpriseForm.deployment_type.length) {
        this.$store.commit("raiseSnackbar", {
          text: "Please complete all required fields.",
          color: "warning",
          timeout: 4000
        });
        return;
      }

      try {
        const response = await Vue.$http.post(
          "api/enterprise-inquiry/",
          null,
          this.enterpriseForm
        );
        const payload = response?.data || response?.body || response;
        this.enterpriseSubmitted = true;
        this.enterpriseCalendlyUrl =
          payload?.calendly_url || this.catalog?.enterprise?.calendly_url || "";
      } catch (error) {
        const status = Number(error?.status || error?.response?.status || 0);
        const retryAfterHeader =
          error?.headers && typeof error.headers.get === "function"
            ? Number(error.headers.get("Retry-After") || 0)
            : 0;
        const retryAfterSeconds = Number(
          error?.retryAfter ||
            error?.body?.retry_after ||
            error?.data?.retry_after ||
            retryAfterHeader ||
            0
        );
        if (status === 429) {
          const retryHint =
            Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
              ? ` Please wait ${Math.ceil(
                  retryAfterSeconds
                )}s before submitting again.`
              : " Please wait a bit before submitting again.";
          this.$store.commit("raiseSnackbar", {
            text: `Too many enterprise inquiry requests.${retryHint}`,
            color: "warning",
            timeout: 6500
          });
          return;
        }

        this.$store.commit("raiseSnackbar", {
          text:
            "Unable to submit your enterprise request right now. Please try again.",
          color: "error",
          timeout: 4000
        });
        console.error(error);
      }
    }
  }
};
</script>

<style scoped>
.pricing-container {
  max-width: 1040px;
  margin: 0 auto;
  padding: 16px 24px;
  color: #f5f5f5;
}
.pricing-container h1,
.pricing-container h2,
.pricing-container h3,
.pricing-container h4,
.pricing-container h5,
.pricing-container h6 {
  margin-top: 0;
}

.title {
  text-align: center;
  font-weight: 700;
  margin-bottom: 8px;
}

.subtitle {
  text-align: center;
  color: #b5b5b5;
  font-size: 14px;
  margin-bottom: 14px;
}

.billing-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.billing-option {
  display: inline-flex;
  align-items: center;
  border: 1px solid #4a4a4a;
  border-radius: 999px;
  padding: 10px 16px;
  background: #2a2a2a;
  color: #f5f5f5;
  cursor: pointer;
}

.billing-option.active {
  border-color: #4395a3;
  background: rgba(67, 149, 163, 0.12);
  color: #4395a3;
}

.discount-label {
  font-size: 10px;
  line-height: 1;
  margin-left: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(67, 149, 163, 0.15);
  color: #4395a3;
  font-weight: 600;
}

.billing-option.active .discount-label {
  background: rgba(67, 149, 163, 0.2);
  color: #4395a3;
  font-weight: 700;
}

.plans {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.usage-policy-note {
  color: #b5b5b5;
  font-size: 12px;
  line-height: 1.45;
  margin: 14px 0 0;
  text-align: center;
  font-style: italic;
}

.plan {
  flex: 1 1 230px;
  min-width: 230px;
}

.card {
  border: 1px solid #3a3a3a;
  border-radius: 12px;
  padding: 16px 20px;
  height: 100%;
  background: #1e1e1e;
  display: flex;
  flex-direction: column;
}

.card .feature-list {
  flex: 1;
}

.card.featured {
  border: 2px solid rgba(67, 149, 163, 0.7);
  background: #1e1e1e;
}

.plan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.current-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #555;
  color: #999;
}

.popular-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(67, 149, 163, 0.12);
  color: #4395a3;
}

.card.active {
  border-color: #555;
}

.plan-title {
  font-size: 18px;
  font-weight: 700;
}

.plan-price {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 4px;
}

.billing-info {
  color: #b5b5b5;
  font-size: 13px;
  margin-bottom: 4px;
}

.billing-info--placeholder {
  visibility: hidden;
}

.subscribe-btn {
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 10px;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  margin: 8px 0;
}

.btn-primary {
  background: #4395a3;
  color: #fff;
}

.btn-outline-primary {
  background: rgba(67, 149, 163, 0.12);
  border: none;
  color: #4395a3;
}

.btn-outline-primary:hover {
  background: rgba(67, 149, 163, 0.2);
}

.btn-current {
  background: transparent;
  border: 1.5px solid #666;
  color: #999;
  cursor: default;
}

.feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 13px;
}

.feature-list li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.feature-check {
  color: #999;
  flex-shrink: 0;
}

.feature-key .feature-text {
  font-weight: 600;
  color: #f5f5f5;
}

.feature-divider-above {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #333;
}

.plan-notes {
  margin: 8px 0 0;
  padding-top: 8px;
  border-top: 1px solid #333;
  color: #999;
  font-size: 10.5px;
  line-height: 1.35;
}

.plan-note {
  color: inherit;
  display: inline;
}

.plan-note-link {
  color: inherit;
  text-decoration: none;
}

.plan-note-link:hover {
  color: #b5b5b5;
  text-decoration: underline;
}

.plan-note--new-line {
  display: block;
  margin-top: 3px;
}

.plan-note-separator {
  margin: 0 6px;
  color: #666;
}

.plan-note--badge {
  color: #777;
  font-style: italic;
  font-weight: 400;
}

.section-divider {
  border: none;
  border-top: 1px solid #333;
  margin: 24px auto 0;
  max-width: 200px;
}

.enterprise-qualifier {
  margin: 24px 0 16px;
  padding: 20px 32px;
  background: #242424;
  border: 1px solid #3a3a3a;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.enterprise-content {
  flex: 1 1;
  min-width: 260px;
  max-width: 720px;
}

.enterprise-title {
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 6px;
}

.enterprise-subtitle {
  color: #d5d5d5;
  margin-bottom: 8px;
  font-weight: 600;
}

.enterprise-description {
  color: #cfcfcf;
  line-height: 1.5;
  font-size: 14px;
  margin-bottom: 0px;
}

.enterprise-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
  margin-top: 16px;
}

.enterprise-btn {
  border: 0;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 700;
  cursor: pointer;
  background: #4395a3;
  color: #fff;
  min-width: 220px;
}

.enterprise-link {
  background: transparent;
  border: 0;
  color: #d5d5d5;
  cursor: pointer;
  padding: 0;
  font-weight: 600;
}

.enterprise-link:hover {
  color: #4395a3;
}

.enterprise-dialog-card {
  background: linear-gradient(165deg, #1e1e1e, #171717 58%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 26px 70px rgba(2, 6, 23, 0.55);
}

.enterprise-dialog-title {
  font-weight: 700;
  font-size: 1.02rem;
  line-height: 1.35;
  letter-spacing: 0.01em;
  padding: 20px 24px 16px;
}

.enterprise-dialog-body {
  padding: 18px 24px 12px;
}

.enterprise-dialog-success {
  padding: 18px 24px 14px;
}

.enterprise-booking-btn {
  box-shadow: 0 10px 24px rgba(67, 149, 163, 0.26);
  border-radius: 12px !important;
  text-transform: none;
  letter-spacing: 0.01em;
  font-weight: 700;
  min-width: 132px;
}

.enterprise-submit-btn {
  font-weight: 700;
  border-radius: 12px !important;
  text-transform: none;
  letter-spacing: 0.01em;
  min-width: 110px;
  box-shadow: 0 10px 24px rgba(67, 149, 163, 0.26);
}

.enterprise-success-note {
  border: 1px solid #3f3f3f;
  border-radius: 12px;
  background: #262626;
  padding: 14px 16px;
}

.enterprise-success-question {
  font-weight: 600;
  color: #d9e1eb;
}

.dialog-close-btn {
  opacity: 0.76;
  font-size: 13px;
  text-transform: none;
  letter-spacing: 0.01em;
  border-radius: 12px !important;
}

.dialog-close-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

::v-deep .enterprise-dialog-card .v-card__actions {
  padding: 14px 20px 20px;
  gap: 8px;
}

::v-deep .enterprise-dialog-card .v-input__slot {
  background: #262626 !important;
  border-color: #3f3f3f !important;
  border-radius: 12px !important;
}

::v-deep
  .enterprise-dialog-card
  .v-text-field--outlined
  > .v-input__control
  > .v-input__slot,
::v-deep
  .enterprise-dialog-card
  .v-text-field--solo
  > .v-input__control
  > .v-input__slot {
  border-radius: 12px !important;
}

::v-deep .enterprise-dialog-card .v-text-field fieldset {
  border-radius: 12px !important;
}

::v-deep .enterprise-dialog-card .v-label {
  color: #cdd6e0 !important;
}

::v-deep .enterprise-dialog-card .v-chip {
  background: #3a3a3a !important;
}

::v-deep .enterprise-inquiry-dialog {
  border-radius: 20px;
}

@media (max-width: 900px) {
  .plan {
    min-width: 260px;
  }
}

@media (max-width: 620px) {
  .plan {
    min-width: 100%;
  }

  .enterprise-actions {
    width: 100%;
  }

  .enterprise-btn {
    width: 100%;
    min-width: 0;
  }
}
</style>
